interface CustomerDetailsFormProps {
  customerDetails: any;
  onCustomerDetailsChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectedLocation: boolean;
}

export function CustomerDetailsForm({
  customerDetails,
  onCustomerDetailsChange,
  selectedLocation,
}: CustomerDetailsFormProps) {
  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const currentNames = (customerDetails?.fullName || '').split(' ');
    let firstName = currentNames[0] || '';
    let lastName = currentNames[1] || '';

    if (name === 'firstName') {
      firstName = value;
    } else if (name === 'lastName') {
      lastName = value;
    }

    const syntheticEvent = {
      target: {
        name: 'fullName',
        value: `${firstName} ${lastName}`.trim()
      }
    } as React.ChangeEvent<HTMLInputElement>;

    onCustomerDetailsChange(syntheticEvent);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const syntheticEvent = {
      target: {
        name: 'email',
        value: value.toLowerCase().trim()
      }
    } as React.ChangeEvent<HTMLInputElement>;

    onCustomerDetailsChange(syntheticEvent);
  };

  const [firstName = '', lastName = ''] = (customerDetails?.fullName || '').split(' ');
  const isEmailValid = !customerDetails?.email || validateEmail(customerDetails.email);
  const isFirstNameValid = firstName.trim() !== '';
  const isLastNameValid = lastName.trim() !== '';
  const isPostCodeValid = !customerDetails?.postCode || customerDetails.postCode.trim() !== '';

  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium text-gray-700 dark:text-gray-300">Customer Details</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <input
            type="text"
            name="firstName"
            placeholder="First Name *"
            value={firstName}
            onChange={handleNameChange}
            className={`w-full h-14 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base px-4 ${
              isFirstNameValid
                ? 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                : 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}
            required
          />
          {!isFirstNameValid && (
            <p className="mt-1 text-sm text-red-500">
              First name is required
            </p>
          )}
        </div>
        <div>
          <input
            type="text"
            name="lastName"
            placeholder="Last Name *"
            value={lastName}
            onChange={handleNameChange}
            className={`w-full h-14 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base px-4 ${
              isLastNameValid
                ? 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                : 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}
            required
          />
          {!isLastNameValid && (
            <p className="mt-1 text-sm text-red-500">
              Last name is required
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={customerDetails?.email || ''}
            onChange={handleEmailChange}
            className={`w-full h-14 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base px-4 ${
              isEmailValid
                ? 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                : 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}
            required
          />
          {!isEmailValid && (
            <p className="mt-1 text-sm text-red-500">
              Please enter a valid email address (e.g., user@example.com)
            </p>
          )}
        </div>
        <div>
          <input
            type="text"
            name="postCode"
            placeholder="Post Code *"
            value={customerDetails?.postCode || ''}
            onChange={onCustomerDetailsChange}
            required
            className={`w-full h-14 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base px-4 ${
              isPostCodeValid
                ? 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                : 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
            }`}
          />
          {!isPostCodeValid && (
            <p className="mt-1 text-sm text-red-500">
              Post code is required
            </p>
          )}
        </div>
      </div>
      <div>
        <input
          type="tel"
          name="phone"
          placeholder="Phone Number *"
          value={customerDetails?.phone || ''}
          onChange={onCustomerDetailsChange}
          className="w-full h-14 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base px-4"
          required
        />
      </div>
      {selectedLocation && (
        <div>
          <textarea
            name="address"
            placeholder="Delivery Address *"
            value={customerDetails?.address || ''}
            onChange={onCustomerDetailsChange}
            className="w-full h-32 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-base p-4"
            required
          />
        </div>
      )}
      <div>
        <textarea
          name="note"
          placeholder="Order Note (Optional)"
          value={customerDetails?.note || ''}
          onChange={onCustomerDetailsChange}
          className="w-full h-32 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-base p-4"
        />
      </div>
    </div>
  );
}